import Vue from 'vue'
import VueRouter from 'vue-router'
import { onAuthStateChanged } from 'firebase/auth'
import store from '../store'
import SignIn from '../components/auth/SignIn.vue'
import NavBar from '../components/NavBar.vue'
import TicketPrint from '../components/ticket/TicketPrint.vue'
import Shift from '../components/shift/Shift.vue'
import Shifts from '../components/shift/Shifts.vue'
import ZXReport from '../components/shift/ZXReport.vue'
import SectionsReport from '../components/shift/SectionsReport.vue'
import OperatorsReport from '../components/shift/OperatorsReport.vue'
import FiscalReport from '../components/shift/FiscalReport.vue'

Vue.use(VueRouter)

const routes = [
  {
    path: '*',
    redirect: '/',
  },
  {
    path: '/signin',
    name: 'SignIn',
    meta: { requiresAuth: false },
    component: SignIn,
  },
  {
    path: '/',
    component: NavBar,
    children: [
      {
        path: '/',
        name: 'Dashboard',
        beforeEnter: (to, from, next) => {
          next(`/shifts/${store.state.cashRegisters.cashRegister.cashRegister.id}`)
        },
      },
      {
        path: '/shifts/:cashRegisterId',
        name: 'Shifts',
        component: Shifts,
      },
    ],
  },
  {
    path: '/print/:cashRegisterId/:ticketId',
    name: 'TicketPrint',
    component: TicketPrint,
    props: true,
  },
  {
    path: '/shifts/:cashRegisterId/:shiftNumber',
    name: 'Shift',
    component: Shift,
  },
  {
    path: '/shifts/:cashRegisterId/:shiftNumber/zxreport',
    name: 'ZXReport',
    component: ZXReport,
  },
  {
    path: '/shifts/:cashRegisterId/:shiftNumber/sections',
    name: 'SectionsReport',
    component: SectionsReport,
  },
  {
    path: '/shifts/:cashRegisterId/:shiftNumber/operators',
    name: 'OperatorsReport',
    component: OperatorsReport,
  },
  {
    path: '/reports/fiscal',
    name: 'FiscalReport',
    component: FiscalReport,
  },
]

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes,
})

// После деплоя старые чанки удаляются с сервера, и у пользователей с открытой вкладкой
// падает динамический импорт — перезагружаем страницу, чтобы подтянуть новую версию
const CHUNK_RELOAD_KEY = 'rekassa.kz-chunk-reload-at'

const isStaleChunkError = (error) => /dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(error && error.message)

const reloadOnStaleChunk = (path) => {
  const lastReloadAt = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10)
  if (Date.now() - lastReloadAt < 60000) {
    return false
  }
  sessionStorage.setItem(CHUNK_RELOAD_KEY, `${Date.now()}`)
  window.location.assign(path || window.location.href)
  return true
}

let pendingFullPath = null

router.beforeEach((to, from, next) => {
  pendingFullPath = to.fullPath
  next()
})

router.onError((error) => {
  if (isStaleChunkError(error)) {
    reloadOnStaleChunk(pendingFullPath)
  }
})

window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnStaleChunk(pendingFullPath)) {
    event.preventDefault()
  }
})

const goSignin = (to, from, next) => {
  window.scrollTo(0, 0)
  next({ path: '/signin', query: { from: to.fullPath } })
}

const goNext = (next, path) => {
  window.scrollTo(0, 0)
  if (path) next(path)
  else next()
}

const getCurrentUser = () => new Promise((resolve) => {
  onAuthStateChanged(store.state.auth.firebaseAuth, (user) => {
    resolve(user)
  })
})

const saveUtmParamsToLocalStorage = (to) => {
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'page_title', 'page_path', 'referrer']
  utmParams.forEach(param => {
    if (to.query[param]) {
      localStorage.setItem(`rekassa.kz-marketing-${param}`, to.query[param])
    }
  })
}

router.beforeEach((to, from, next) => {
  saveUtmParamsToLocalStorage(to)

  const isAuthenticated = store.state.auth.user !== null
  if (to.meta.requiresAuth !== false && !isAuthenticated) {
    // Cordova
    if (window.cordova !== undefined) {
      if (!navigator.onLine) {
        store.dispatch('tools/setConnectionWarning', true)
      }

      window.FirebasePlugin.getCurrentUser((user) => {
        if (user) {
          store.dispatch('auth/setUser', user).then(() => {
            store.dispatch('cashRegisters/init', to).then(() => {
              goNext(next)
            }).catch(() => {
              // Стор не инициализирован (сеть/сервер) — не рендерим приложение с пустой кассой
              store.dispatch('tools/setConnectionWarning', true)
              next(false)
            })
          })
        } else {
          goSignin(to, from, next)
        }
      }, () => {
        goSignin(to, from, next)
      })

    // Web
    } else {
      getCurrentUser().then((user) => {
        if (user) {
          store.dispatch('auth/setUser', user).then(() => {
            store.dispatch('cashRegisters/init', to).then(() => {
              goNext(next)
            }).catch(() => {
              // Стор не инициализирован (сеть/сервер) — не рендерим приложение с пустой кассой
              store.dispatch('tools/setConnectionWarning', true)
              next(false)
            })
          })
        } else {
          goSignin(to, from, next)
        }
      }, () => {
        goSignin(to, from, next)
      })
    }
  } else {
    goNext(next)
  }
})

// Track screens
router.afterEach((to) => {
  store.dispatch('analytics/setScreenName', to.name)
})

export default router
